import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Spinner } from "../../shared/ui";
import { Shield, Save, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Required Tickets editor for a single machine. Shown inside the machine
 * detail tab. Admin/manager/office can toggle which tickets must be held
 * (non-expired) by any operator clocking onto this machine.
 */
export const MachineRequiredTicketsEditor = ({ machine, currentUser, onSaved }) => {
  const [allTickets, setAllTickets] = useState([]);
  const [selected, setSelected] = useState(new Set(machine?.required_tickets || []));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canEdit = ["admin", "manager", "office_staff"].includes(currentUser?.role);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/ticket-types`);
        if (!cancel) setAllTickets(r.data || []);
      } catch {
        toast.error("Couldn't load tickets");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => { setSelected(new Set(machine?.required_tickets || [])); }, [machine?.id]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/machines/${machine.id}/required-tickets`, { ticket_ids: Array.from(selected) });
      toast.success("Required tickets updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;

  const grouped = allTickets.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});
  const cats = Object.keys(grouped).sort();

  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="machine-required-tickets">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-bold uppercase text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-orange-500" /> Required Tickets to Operate
          </h4>
          <p className="text-xs text-slate-500 mt-1">
            Operator must hold every ticket selected below (non-expired) to clock onto this machine.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-required-tickets-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        )}
      </div>

      {selected.size === 0 ? (
        <div className="text-xs italic text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
          ⚠️ No tickets required. Any user can operate this machine.
        </div>
      ) : (
        <div className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded p-2 mb-3">
          ✓ {selected.size} ticket{selected.size > 1 ? "s" : ""} required to operate
        </div>
      )}

      <div className="space-y-3">
        {cats.map((cat) => (
          <div key={cat}>
            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">{cat}</div>
            <div className="grid sm:grid-cols-2 gap-1">
              {grouped[cat].map((t) => {
                const isOn = selected.has(t.id);
                return (
                  <label
                    key={t.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition border ${
                      isOn ? "bg-orange-100 border-orange-400" : "bg-white border-slate-200 hover:border-slate-400"
                    } ${!canEdit ? "cursor-default" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isOn}
                      disabled={!canEdit}
                      onChange={() => toggle(t.id)}
                      data-testid={`ticket-req-${t.code}`}
                    />
                    <span className="text-sm">
                      {t.name}
                      <span className="ml-1 text-[10px] text-slate-500 font-mono">{t.code}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MachineRequiredTicketsEditor;
